import { Flex, Skeleton, Text } from '@mantine/core';
import { PageAbsoluteContainer } from '@dealstack/frontend/shared/layout/PageAbsoluteContainer/PageAbsoluteContainer';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';
import { StyledIcon } from '@dealstack/frontend/shared/layout/DashboardGrid/styled.tsx';
import { SecurityPill } from '@dealstack/frontend/shared/components/SecurityPill/SecurityPill.tsx';
import { InvestorDetailsColumns } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/InvestorDetailsV2.styles.tsx';
import { PageHeader } from '@dealstack/frontend/shared/layout/PageHeader/PageHeader.tsx';
import { useInvestmentVehicleController } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestmentVehicleDetails/useInvestmentVehicleController.ts';
import { VehicleProfileCard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestmentVehicleDetails/components/VehicleProfileCard/VehicleProfileCard.tsx';
import { AddressesCard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/AddressesCard/AddressesCard.tsx';
import { TaxRecordsCard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/TaxRecordsCard/TaxRecordsCard.tsx';
import { BankAccountsCard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/BankAccountsCard/BankAccountsCard.tsx';
import { RecentActivityCard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/RecentActivityCard/RecentActivityCard.tsx';
import { VehicleHoldingsSummaryCard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestmentVehicleDetails/components/VehicleHoldingsSummaryCard/VehicleHoldingsSummaryCard.tsx';
import { VehicleHoldingsCard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestmentVehicleDetails/components/VehicleHoldingsCard/VehicleHoldingsCard.tsx';
import { VehicleLoansCard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestmentVehicleDetails/components/VehicleLoansCard/VehicleLoansCard.tsx';
import { StorageKeys } from '@dealstack/frontend/shared/helper/storageUtils.ts';
import { VehicleQuickLinksCard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestmentVehicleDetails/components/VehicleQuickLinksCard/VehicleQuickLinksCard.tsx';
import { RelatedPeopleCard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestmentVehicleDetails/components/RelatedPeopleCard/RelatedPeopleCard.tsx';
import { DocumentsCard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/DocumentsCard/DocumentsCard.tsx';

export const InvestmentVehicleDetails = () => {
	const tMipManage = useTranslationMipManage();
	const {
		dealId,
		mipId,
		personId,
		documentPersonIds,
		vehicle,
		isLoading,
		isDetailsColumnCollapsed,
		toggleDetailsColumn,
	} = useInvestmentVehicleController();

	const pageTitle = (
		<Flex align="baseline" gap="0.5rem">
			<StyledIcon
				dataTestId={TEST_ELEMENT_IDS.TOGGLE_PANEL_ICON}
				variant={isDetailsColumnCollapsed ? 'expand-left-panel' : 'collapse-left-panel'}
				size="xl"
				onClick={toggleDetailsColumn}
			/>

			<Flex gap="0.75rem" align="center">
				{isLoading ? (
					<Skeleton height={32} width={320} radius="sm" />
				) : (
					<>
						<Text
							fz={32}
							fw={600}
							c="dark.8"
							data-testid={
								TEST_ELEMENT_IDS.MIP_MANAGE_INVESTMENT_VEHICLE_DETAILS_NAME
							}
						>
							{vehicle?.displayName}
						</Text>

						<SecurityPill
							label={tMipManage('investors.details.investment_vehicle.piv_badge', {
								defaultValue: 'PIV',
							})}
							data-testid={
								TEST_ELEMENT_IDS.MIP_MANAGE_INVESTMENT_VEHICLE_DETAILS_PIV_BADGE
							}
						/>
					</>
				)}
			</Flex>
		</Flex>
	);

	return (
		<PageAbsoluteContainer>
			<PageHeader customTitle={pageTitle} hideBreadcrumbs />

			<InvestorDetailsColumns $detailsCollapsed={isDetailsColumnCollapsed}>
				<aside className="column details">
					<div className="column-content">
						<VehicleProfileCard
							dealId={dealId}
							vehicle={vehicle}
							isLoading={isLoading}
						/>

						<AddressesCard addresses={vehicle?.addresses} />

						<TaxRecordsCard personId={personId} />

						<BankAccountsCard personId={personId} />
					</div>
				</aside>

				<section className="column main">
					<div className="column-content">
						<VehicleHoldingsSummaryCard personId={personId} />

						<VehicleHoldingsCard
							dealId={dealId}
							personId={personId}
							vehicleName={vehicle?.displayName}
						/>

						<VehicleLoansCard dealId={dealId} personId={personId} />

						<RelatedPeopleCard dealId={dealId} mipId={mipId} personId={personId} />

						<DocumentsCard
							viewStorageKey={
								StorageKeys.MIP_INVESTMENT_VEHICLE_DETAILS_DOCUMENTS_VIEW
							}
							dealId={dealId}
							mipId={mipId}
							personIds={documentPersonIds}
						/>

						<VehicleQuickLinksCard dealId={dealId} mipId={mipId} personId={personId} />
					</div>
				</section>

				<aside className="column activity">
					<div className="column-content">
						<RecentActivityCard
							dealId={dealId}
							personId={personId}
							isLoading={isLoading}
						/>
					</div>
				</aside>
			</InvestorDetailsColumns>
		</PageAbsoluteContainer>
	);
};
